import { ReactElement, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import { Button } from '../../../components/Button/Button';
import classes from './PartnerOverviewPage.module.css';
import { Alert } from '../../../components/Alert/Alert';
import { useGetPartnersQuery } from '../../../hooks/queries/useGetPartnersQuery';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { PartnerTable } from '../../../components/PartnerTable';
import { Routes } from '../../../enums/Routes';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { calculateTotalCustomers } from '../../../utils/partnerHelpers';

const PAGE_TITLE = 'Partneroversikt';
const PAGE_DESCRIPTION = 'Her kan du administrere alle partnere i Trustpulse-systemet';
const ERROR_MESSAGE = 'Kunne ikke laste partnere. Prøv igjen senere.';
const ADD_PARTNER_BUTTON = 'Legg til ny partner';

const AVERAGE_USERS_PER_PARTNER = 3; // TODO: Replace with real API data

export const PartnerOverviewPage = (): ReactElement => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetPartnersQuery();

  const handleCreatePartner = () => {
    navigate(Routes.SystemAdminCreatePartner);
  };

  const totalCustomers = useMemo(() => {
    if (!data?.partners) return 0;
    return calculateTotalCustomers(data.partners);
  }, [data?.partners]);

  // Mock: Antall aktive partner-brukere (backend endpoint mangler)
  // Temporary estimation until backend provides actual count
  const totalPartnerUsers = useMemo(() => {
    if (!data?.partners) return 0;
    return data.partners.length * AVERAGE_USERS_PER_PARTNER;
  }, [data?.partners]);

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <div className={classes.headerContent}>
          <div>
            <Heading as="h1">{PAGE_TITLE}</Heading>
            <Paragraph size="lg">{PAGE_DESCRIPTION}</Paragraph>
          </div>
          <Button variant="primary" size="medium" onClick={handleCreatePartner}>
            {ADD_PARTNER_BUTTON}
          </Button>
        </div>
      </header>

      <div className={classes.content}>
        {isLoading && <GhostLoader />}

        {error && (
          <Alert variant="danger" size="md">
            <Paragraph>{ERROR_MESSAGE}</Paragraph>
          </Alert>
        )}

        {data && !isLoading && (
          <>
            <div className={classes.statsGrid}>
              <ContainerCard>
                <ContainerCard.Title>Totalt antall partnere</ContainerCard.Title>
                <ContainerCard.Body className={classes.statBody}>
                  <div className={classes.statValue}>{data.total}</div>
                </ContainerCard.Body>
              </ContainerCard>

              <ContainerCard>
                <ContainerCard.Title>Totalt antall kunder</ContainerCard.Title>
                <ContainerCard.Body className={classes.statBody}>
                  <div className={classes.statValue}>{totalCustomers}</div>
                  <div className={classes.statMeta}>under partnere</div>
                </ContainerCard.Body>
              </ContainerCard>

              <ContainerCard>
                <ContainerCard.Title>Partner-brukere</ContainerCard.Title>
                <ContainerCard.Body className={classes.statBody}>
                  <div className={classes.statValue}>{totalPartnerUsers}</div>
                  <div className={classes.statMeta}>aktive brukere</div>
                </ContainerCard.Body>
              </ContainerCard>
            </div>

            <ContainerCard>
              <ContainerCard.Title>Partnere</ContainerCard.Title>
              <ContainerCard.Body noPadding>
                <PartnerTable partners={data.partners} />
              </ContainerCard.Body>
            </ContainerCard>
          </>
        )}
      </div>
    </div>
  );
};
